<template>
  <div>
    <!-- General / Auto Cancel Order Section -->
    <b-card class="mb-2">
      <validation-observer v-slot="{ handleSubmit, valid }">
        <b-card-text class="mb-2">
          <h4>{{ $t('general_configuration') }}</h4>
        </b-card-text>

        <p class="mb-1">
          {{ $t('auto_cancel_general_desc') }}
        </p>

        <validation-provider
          v-slot="{ errors }"
          :name="$t('auto_cancel_after_label')"
          rules="required|integer_order_hours|min_value_order_hours:1"
        >
          <b-form-group>
            <label class="d-block">
              <span>{{ $t('auto_cancel_after_label') }}</span>
            </label>
            <div class="d-flex align-items-center">
              <b-form-input
                v-model.number="autoCancelAfterHours"
                type="number"
                class="mr-1"
                style="max-width: 120px"
                :state="errors.length === 0 ? null : false"
              />
              <span>{{ $t('auto_cancel_after_hours') }}</span>
            </div>
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <div class="d-flex flex-row justify-content-end mt-2">
          <b-button
            variant="primary"
            :loading="updateServiceSystemLoading"
            :disabled="updateServiceSystemLoading || !valid"
            @click="handleSubmit(updateServiceSystemConfig)"
          >
            {{ $t('button.saveChanges') }}
          </b-button>
        </div>
      </validation-observer>
    </b-card>

    <!-- Service System Config Section -->
    <b-card class="mb-2">
      <div>
        <text-editor
          :id="message.id"
          key="service_system_config"
          v-model="message.options"
          :title="$t('chat_configuration')"
          :style="{ width: '100%' }"
          :desc="$t('service_system_config_desc')"
          @update:textEditor="onUpdateTextEditor"
          @validation="onValidate"
        />
      </div>

      <div class="col-12 col-lg-8 col-md-10 mt-1 p-0">
        <validation-provider
          v-slot="{ errors }"
          name="Keywords"
        >
          <b-form-group :description="$t('label.keywordInstruction')">
            <label class="d-block">
              <span>{{ $t('field.keyword') }}</span>
              <span class="text-muted"> - {{ $t('field.optional') }} </span>
            </label>

            <b-form-tags
              v-model="serviceSystemConfig.keywords"
              separator=",;"
              input-class="form-control form-tags"
              no-add-on-enter
              :placeholder="$t('placeholder.keyword')"
              :remove-on-delete="true"
              :duplicate-tag-text="$t('error.duplicateTags')"
              :add-button-text="$t('button.add')"
              :state="errors.length === 0 ? null : false"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>
      </div>

      <div class="d-flex flex-row justify-content-end mt-2">
        <b-button
          variant="primary"
          :loading="updateServiceSystemLoading"
          :disabled="updateServiceSystemLoading || !isValidNotifySend"
          @click="updateServiceSystemConfig"
        >
          {{ $t('button.saveChanges') }}
        </b-button>
      </div>
    </b-card>

    <!-- Service System Integration Section -->
    <b-card>
      <validation-observer
        ref="formValidator"
        v-slot="{ handleSubmit, errors, valid }"
      >
        <b-card-text class="mb-2">
          <h4>{{ $t('webhook_configuration') }}</h4>
        </b-card-text>

        <div class="row mb-1">
          <div class="col-12">
            <div class="d-flex align-items-center">
              <b-form-checkbox
                v-model="serviceSystemConfig.isEnableNotifyStatusCallback"
                name="callback-toggle"
                switch
              >
                <span class="font-weight-bold">
                  {{
                    $t(
                      `${
                        serviceSystemConfig.isEnableNotifyStatusCallback
                          ? 'button.enable'
                          : 'button.disable'
                      }`
                    )
                  }}
                </span>
              </b-form-checkbox>
            </div>
          </div>
        </div>
        <b-card-text class="mb-2">
          {{ $t('enable_notify_desc') }}
        </b-card-text>

        <div class="row">
          <div class="col-12 col-lg-8 col-md-10 mb-1">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('label.notifyUrl')"
              :rules="urlRules"
            >
              <b-form-group
                :label="$t('label.notifyUrl')"
                label-for="callbackUrl"
                :description="$t('label.notifyUrlInInstruction')"
              >
                <b-form-input
                  id="callbackUrl"
                  v-model="serviceSystemConfig.callbackUrl"
                  type="url"
                  :disabled="!serviceSystemConfig.isEnableNotifyStatusCallback"
                  placeholder="https://example.com/callback"
                  :state="errors.length === 0 ? null : false"
                  trim
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </div>

          <div class="col-12 col-lg-8 col-md-10 mb-2">
            <validation-provider
              v-slot="{ errors }"
              name="Auth Key"
              :rules="authKeyRules"
            >
              <b-form-group :description="$t('label.authorizationInstruction')">
                <label class="d-block">
                  <span>{{ $t('callback_auth_key') }}</span>
                  <span class="text-muted"> - {{ $t('field.optional') }} </span>
                </label>

                <b-form-input
                  id="callbackAuthKey"
                  v-model="serviceSystemConfig.callbackAuthKey"
                  type="text"
                  :disabled="!serviceSystemConfig.isEnableNotifyStatusCallback"
                  :state="errors.length === 0 ? null : false"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </div>
        </div>

        <div class="d-flex flex-row justify-content-end mt-2">
          <b-button
            variant="primary"
            :loading="updateServiceSystemLoading"
            :disabled="updateServiceSystemLoading || !valid"
            @click="handleSubmit(updateServiceSystemIntegrate)"
          >
            {{ $t('button.saveChanges') }}
          </b-button>
        </div>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import * as rules from 'vee-validate/dist/rules'
import isURL from 'validator/lib/isURL'
import {
  BCard,
  BCardText,
  BFormGroup,
  BFormInput,
  BButton,
  BFormCheckbox,
  BFormTags,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import TextEditor from '../TextEditor.vue'
import i18n from '@/libs/i18n'

const ChatAssignmentRepository = Repository.get('chatAssignmentRepository')

export default {
  name: 'ConfigContent',
  components: {
    ValidationProvider,
    ValidationObserver,
    BCard,
    BCardText,
    BFormGroup,
    BFormInput,
    BButton,
    BFormCheckbox,
    BFormTags,
    TextEditor,
  },
  beforeCreate() {
    // Create custom min_value_order_hours rule for hours field validation
    // This extends the min_value rule with a custom message function
    extend('min_value_order_hours', {
      ...rules.min_value,
      message(fieldName, placeholders) {
        // Always return the custom hours field message for this rule
        return i18n.t('error.hoursFieldMustBeOneOrMore')
      }
    })

    extend('integer_order_hours', {
      ...rules.integer,
      message(fieldName, placeholders) {
        return i18n.t('error.integerOrderHours')
      }
    })

    extend('required_notify', {
      ...rules.required,
      message(fieldName, placeholders) {
        return i18n.t('error.requiredNotify')
      }
    })

    extend('url_notify', {
      ...rules.url,
      validate(value) {
        if (value) {
          return isURL(value, { require_tld: true })
        }

        return false
      },
      message(fieldName, placeholders) {
        return i18n.t('error.urlNotify')
      }
    })
  },
  data() {
    return {
      message: {
        id: '',
        title: '',
        desc: '',
        options: {
          notifySend: 2,
          autoCloseResolve: 2,
          enable: false,
          isEnableAutoCloseChatAfterResolved: false,
          locales: {
            en: '',
            zh: '',
            km: '',
            vi: '',
          },
        },
      },
      serviceSystemConfig: {
        message: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        notifySend: 1,
        autoCloseResolve: 5,
        autoCancelAfterMin: 1440, // 24 hours in minutes
        isEnableNotifyStatusCallback: false,
        callbackUrl: null,
        callbackAuthKey: null,
        isEnableMessage: false,
        keywords: [],
      },
      updateServiceSystemLoading: false,
      isValidNotifySend: true,
    }
  },
  computed: {
    urlRules() {
      if (!this.serviceSystemConfig.isEnableNotifyStatusCallback) return ''
      return 'required_notify|url_notify'
    },
    authKeyRules() {
      if (!this.serviceSystemConfig.isEnableNotifyStatusCallback) return ''
      return ''
    },
    autoCancelAfterHours: {
      get() {
        const minutes = this.serviceSystemConfig.autoCancelAfterMin
        if (!minutes && minutes !== 0) return null
        return minutes / 60
      },
      set(value) {
        const num = Number(value)
        if (Number.isNaN(num)) {
          this.serviceSystemConfig.autoCancelAfterMin = null
          return
        }
        this.serviceSystemConfig.autoCancelAfterMin = num * 60
      },
    },
  },
  methods: {
    async onUpdateTextEditor(event) {
      this.serviceSystemConfig.message = event.locales
      this.message.options.locales = event.locales
      this.message.options.enable = event.enable
      this.message.options.isEnableAutoCloseChatAfterResolved = event.isEnableAutoCloseChatAfterResolved
    },
    onValidate({ isValid }) {
      this.isValidNotifySend = isValid
    },
    async updateServiceSystemConfig() {
      this.updateServiceSystemLoading = true
      try {
        await ChatAssignmentRepository.update({
          'serviceSystemConfig.message': this.message.options.locales,
          'serviceSystemConfig.notifySend': Number(this.message.options.notifySend) || 0,
          'serviceSystemConfig.autoCloseResolve': Number(this.message.options.autoCloseResolve) || 0,
          'serviceSystemConfig.isEnableMessage': this.message.options.enable,
          'serviceSystemConfig.isEnableAutoCloseChatAfterResolved': this.message.options.isEnableAutoCloseChatAfterResolved,
          'serviceSystemConfig.autoCancelAfterMin':
            Number(this.serviceSystemConfig.autoCancelAfterMin) || 1440,
          'serviceSystemConfig.keywords': this.serviceSystemConfig.keywords,
        })
        this.$emit('refresh')
      } catch (error) {
        console.error(error)
      } finally {
        this.updateServiceSystemLoading = false
      }
    },
    async updateServiceSystemIntegrate() {
      this.updateServiceSystemLoading = true
      try {
        await ChatAssignmentRepository.update({
          'serviceSystemConfig.isEnableNotifyStatusCallback':
            this.serviceSystemConfig.isEnableNotifyStatusCallback,
          'serviceSystemConfig.callbackUrl': this.serviceSystemConfig.callbackUrl,
          'serviceSystemConfig.callbackAuthKey':
            this.serviceSystemConfig.callbackAuthKey,
        })
        this.$emit('refresh')
      } catch (error) {
        console.error(error)
      } finally {
        this.updateServiceSystemLoading = false
      }
    },
    updateData(data) {
      if (!data) return

      this.serviceSystemConfig = {
        ...this.serviceSystemConfig,
        ...data,
      }
      this.message.options.locales = {
        ...this.message.options.locales,
        ...data.message,
      }
      this.message.options.autoCloseResolve = data.autoCloseResolve
      this.message.options.notifySend = data.notifySend
      if (data.autoCancelAfterMin !== undefined && data.autoCancelAfterMin !== null) {
        this.serviceSystemConfig.autoCancelAfterMin = data.autoCancelAfterMin
      }
      this.serviceSystemConfig.message = {
        ...this.serviceSystemConfig.message,
        ...data.message,
      }
      this.message.options.enable = data.isEnableMessage
      this.message.options.isEnableAutoCloseChatAfterResolved = data.isEnableAutoCloseChatAfterResolved
    },
  },
  mounted() {
    this.$emit('refresh')
  },
}
</script>

<style scoped>
.form-tags.form-control {
  height: calc(2.714rem - 0.44rem * 2);
}
.form-tags.form-control:focus {
  box-shadow: none !important;
}
</style>